import React, { useState } from "react";
import axios from "axios";
import { Send, Loader2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactForm = ({ pathKey = "women" }) => {
  const { t, lang } = useLanguage();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const isMen = pathKey === "men";

  const baseField = isMen
    ? "w-full bg-transparent border-b border-[#2C313D] focus:border-[#D4AF37] outline-none py-3 text-white placeholder:text-[#5b6273] transition"
    : "w-full bg-transparent border-b border-[#C9C2BC] focus:border-[#738678] outline-none py-3 text-[#2C332E] placeholder:text-[#9aa39d] transition";

  const labelCls = isMen
    ? "text-[0.7rem] uppercase tracking-[0.24em] text-[#8A91A5]"
    : "text-[0.7rem] uppercase tracking-[0.24em] text-[#5C6660]";

  const submitBtn = isMen ? "btn-gold" : "btn-sage";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, { ...form, path: pathKey, language: lang });
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(t.contact.error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      data-testid={`contact-form-${pathKey}`}
      onSubmit={handleSubmit}
      className="space-y-7 w-full max-w-xl"
    >
      <div>
        <label className={labelCls} htmlFor={`name-${pathKey}`}>{t.contact.name}</label>
        <input
          data-testid={`contact-name-${pathKey}`}
          id={`name-${pathKey}`}
          required
          minLength={2}
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={baseField}
          placeholder=""
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        <div>
          <label className={labelCls} htmlFor={`email-${pathKey}`}>{t.contact.email}</label>
          <input
            data-testid={`contact-email-${pathKey}`}
            id={`email-${pathKey}`}
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className={baseField}
            placeholder=""
          />
        </div>
        <div>
          <label className={labelCls} htmlFor={`phone-${pathKey}`}>{t.contact.phone}</label>
          <input
            data-testid={`contact-phone-${pathKey}`}
            id={`phone-${pathKey}`}
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className={baseField}
            placeholder=""
          />
        </div>
      </div>
      <div>
        <label className={labelCls} htmlFor={`msg-${pathKey}`}>{t.contact.message}</label>
        <textarea
          data-testid={`contact-message-${pathKey}`}
          id={`msg-${pathKey}`}
          required
          minLength={5}
          rows={4}
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          className={baseField + " resize-none"}
          placeholder={t.contact.placeholderMessage}
        />
      </div>

      <div className="flex flex-wrap items-center gap-4 pt-2">
        <button
          data-testid={`contact-submit-${pathKey}`}
          type="submit"
          disabled={submitting}
          className={submitBtn}
        >
          {submitting ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
          {t.contact.submit}
        </button>
        <a
          data-testid={`whatsapp-cta-${pathKey}`}
          href="https://wa.me/4935116087387"
          target="_blank"
          rel="noreferrer"
          className={isMen ? "btn-ghost-dark" : "btn-ghost-light"}
        >
          {t.contact.whatsapp}
        </a>
        <a
          data-testid={`mail-cta-${pathKey}`}
          href="mailto:info@green-horizon-life.com"
          className={isMen ? "btn-ghost-dark" : "btn-ghost-light"}
        >
          {t.contact.mail}
        </a>
      </div>
    </form>
  );
};

export default ContactForm;
